import { useState } from "react";
import { useRoute } from "wouter";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, Edit2, Trash2, Plus } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { ImageUpload } from "@/components/ImageUpload";
import type { FirestoreGamePackage } from "@/../../server/db-games-firestore";
import type { FirestoreGame } from "@/../../server/db-games-firestore";

type GamePackage = FirestoreGamePackage;
type GameData = FirestoreGame;

interface LegacyGamePackage {
  id: number;
  gameKey: string;
  productId: string;
  name: string;
  price: number;
  bonusInfo?: string | null;
  status: "active" | "inactive";
  createdAt: Date;
  updatedAt: Date;
}

export default function AdminGameDetail() {
  const [, params] = useRoute("/admin/games/:gameKey");
  const [, setLocation] = useLocation();
  const gameKey = params?.gameKey as string;

  const [showPackageModal, setShowPackageModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<GamePackage | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditingSaving, setIsEditingSaving] = useState(false);
  const [isSavingPackage, setIsSavingPackage] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    category: "",
    tier: "S" as "S" | "A" | "B" | "C",
    status: "active" as "active" | "inactive",
    description: "",
    imageFile: undefined as File | undefined,
    imageBase64: undefined as string | undefined,
    imagePreview: undefined as string | undefined,
  });
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [packageFormData, setPackageFormData] = useState({
    name: "",
    price: "",
    bonusInfo: "",
    status: "active" as "active" | "inactive",
  });
  const [selectedPackageIds, setSelectedPackageIds] = useState<Set<string>>(new Set());
  const [showBulkStatusDialog, setShowBulkStatusDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<"active" | "inactive">("active");
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Firestore mutations
  const deleteImageMutation = trpc.admin.gamesFirestore.deleteImage.useMutation();
  const deleteGameMutation = trpc.admin.gamesFirestore.deleteGame.useMutation();
  const updateGameMutation = trpc.admin.gamesFirestore.updateGame.useMutation();
  const uploadImageMutation = trpc.admin.gamesFirestore.uploadImage.useMutation();
  const createPackageMutation = trpc.admin.gamesFirestore.createPackage.useMutation();
  const updatePackageMutation = trpc.admin.gamesFirestore.updatePackage.useMutation();
  const deletePackageMutation = trpc.admin.gamesFirestore.deletePackage.useMutation();

  // Fetch game details from Firestore by gameKey
  const { data: game, isLoading: isLoadingGame, refetch: refetchGame } = trpc.admin.gamesFirestore.getGame.useQuery(
    { id: gameKey || "" },
    { enabled: !!gameKey }
  );

  // Fetch game packages from Firestore by gameKey
  const { data: packagesData = [], isLoading: isLoadingPackages, refetch: refetchPackages } = trpc.admin.gamesFirestore.listPackages.useQuery(
    { gameId: gameKey || "" },
    { enabled: !!gameKey }
  );

  const packages: GamePackage[] = packagesData || [];

  const getTierBadge = (tier: string) => {
    const tierColors: Record<string, string> = {
      S: "bg-red-600",
      A: "bg-orange-600",
      B: "bg-yellow-600",
      C: "bg-blue-600",
    };
    return <Badge className={tierColors[tier] || "bg-gray-600"}>{tier}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    return status === "active" ? (
      <Badge className="bg-green-600">Active</Badge>
    ) : (
      <Badge className="bg-red-600">Inactive</Badge>
    );
  };

  const handleAddPackage = () => {
    setSelectedPackage(null);
    setPackageFormData({ name: "", price: "", bonusInfo: "", status: "active" });
    setShowPackageModal(true);
  };

  const handleEditPackage = (pkg: GamePackage) => {
    setSelectedPackage(pkg);
    setPackageFormData({
      name: pkg.packageName,
      price: pkg.price.toString(),
      bonusInfo: pkg.bonusInfo || "",
      status: pkg.status,
    });
    setShowPackageModal(true);
  };

  const validatePackageForm = (): string | null => {
    if (!packageFormData.name.trim()) {
      return "Package name is required";
    }
    if (packageFormData.name.trim().length < 2) {
      return "Package name must be at least 2 characters";
    }
    if (packageFormData.name.trim().length > 100) {
      return "Package name must not exceed 100 characters";
    }
    const price = parseFloat(packageFormData.price);
    if (isNaN(price) || price <= 0) {
      return "Price must be a positive number";
    }
    if (packageFormData.bonusInfo && packageFormData.bonusInfo.trim().length > 255) {
      return "Bonus info must not exceed 255 characters";
    }
    return null;
  };

  const handleSavePackage = async () => {
    if (!selectedPackage) return;

    const validationError = validatePackageForm();
    if (validationError) {
      alert(`Validation Error: ${validationError}`);
      return;
    }

    setIsSavingPackage(true);
    try {
      const price = parseFloat(packageFormData.price);
      await updatePackageMutation.mutateAsync({
        gameId: game?.id || "",
        packageId: selectedPackage.id || "",
        price: price,
        bonusInfo: packageFormData.bonusInfo || undefined,
        packageName: packageFormData.name,
      });
      alert("Package updated successfully!");
      setShowPackageModal(false);
      // TODO: Refetch game packages data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update package";
      console.error("Update package error:", errorMessage);
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsSavingPackage(false);
    }
  };

  const handleEditGameClick = () => {
    if (game) {
      setEditFormData({
        name: game.name,
        category: game.category || "",
        tier: game.tier,
        status: game.status,
        description: game.description || "",
        imageFile: undefined,
        imageBase64: undefined,
        imagePreview: game.image || undefined,
      });
      setShowEditModal(true);
    }
  };

  const validateEditForm = (): string | null => {
    if (!editFormData.name.trim()) {
      return "Game name is required";
    }
    if (editFormData.name.trim().length < 2) {
      return "Game name must be at least 2 characters";
    }
    if (editFormData.name.trim().length > 100) {
      return "Game name must not exceed 100 characters";
    }
    if (editFormData.category && editFormData.category.trim().length > 50) {
      return "Category must not exceed 50 characters";
    }
    if (editFormData.description && editFormData.description.trim().length > 500) {
      return "Description must not exceed 500 characters";
    }
    return null;
  };

  const handleSaveEdit = async () => {
    if (!game) return;

    const validationError = validateEditForm();
    if (validationError) {
      console.error("Validation error:", validationError);
      alert(`Validation Error: ${validationError}`);
      return;
    }

    setIsEditingSaving(true);
    try {
      // Upload image first if selected
      if (editFormData.imageBase64 && editFormData.imageFile) {
        setIsUploadingImage(true);
        try {
          await uploadImageMutation.mutateAsync({
            gameId: game.id || "",
            imageBuffer: await editFormData.imageFile.arrayBuffer().then(ab => Buffer.from(ab)),
            filename: editFormData.imageFile.name,
            contentType: editFormData.imageFile.type || "image/jpeg",
          });
        } finally {
          setIsUploadingImage(false);
        }
      }

      // Update game details
      await updateGameMutation.mutateAsync({
        id: game.id || "",
        name: editFormData.name || undefined,
        category: editFormData.category || undefined,
        tier: editFormData.tier,
        status: editFormData.status,
        description: editFormData.description || undefined,
      });
      setShowEditModal(false);
      alert("Game updated successfully!");
      // Refetch game data
      // TODO: Add refetch logic
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update game";
      console.error("Update game error:", errorMessage);
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsEditingSaving(false);
    }
  };

  const handleDeleteGame = async () => {
    if (!game) return;

    setIsDeleting(true);
    try {
      const result = await deleteGameMutation.mutateAsync({ id: game.id || "" });

      if (result.success) {
        console.log("Game deleted successfully");
        // Navigate back to game list after successful deletion
        setLocation("/admin/games");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete game";
      console.error("Delete game error:", errorMessage);
      // Show error but keep dialog open for retry
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoadingGame) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!game) {
    return (
      <AdminLayout>
        <div className="space-y-4">
        <Button variant="outline" onClick={() => setLocation("/admin/games")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="text-center py-12">
          <p className="text-gray-500">Game not found</p>
        </div>
      </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => setLocation("/admin/games")} className="backoffice-button-outline">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-black">{game.name}</h1>
            <p className="text-black">Game ID: {game.gameId}</p>
          </div>
        </div>

        {/* Game Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Info */}
          <Card className="md:col-span-2 backoffice-card">
            <CardHeader className="backoffice-card-header">
              <CardTitle className="text-black">Game Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-black">Game Key</label>
                  <p className="text-black">{game.gameKey}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-black">Game ID</label>
                  <p className="text-black">{game.gameId}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-black">Category</label>
                  <p className="text-black">{game.category || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-black">Tier</label>
                  <p>{getTierBadge(game.tier)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-black">Status</label>
                  <p>{getStatusBadge(game.status)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-black">Sort Order</label>
                  <p className="text-black">{game.sortOrder}</p>
                </div>
              </div>

              {game.description && (
                <div>
                  <label className="text-sm font-medium text-black">Description</label>
                  <p className="text-black text-sm">{game.description}</p>
                </div>
              )}

              {game.userIdLabel && (
                <div>
                  <label className="text-sm font-medium text-black">User ID Label</label>
                  <p className="text-black">{game.userIdLabel}</p>
                </div>
              )}

              {game.userIdHint && (
                <div>
                  <label className="text-sm font-medium text-black">User ID Hint</label>
                  <p className="text-black text-sm">{game.userIdHint}</p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={handleEditGameClick} className="border-2 border-black text-black hover:text-black">
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Game
                </Button>
              <Button
                onClick={handleDeleteGame}
                disabled={isDeleting}
                className="bg-white border-2 border-red-500 text-red-500 hover:bg-red-50"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Game
                  </>
                )}
              </Button>
              </div>
            </CardContent>
          </Card>

          {/* Game Image */}
          <Card className="bg-white border-2 border-black">
            <CardHeader>
              <CardTitle className="text-black">Game Image</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {game.image ? (
                <img
                  src={game.image}
                  alt={game.name}
                  className="w-full aspect-square object-cover border-2 border-black"
                />
              ) : (
                <div className="w-full aspect-square bg-gray-100 border-2 border-black flex items-center justify-center">
                  <p className="text-gray-500">No image</p>
                </div>
              )}
              {game.imageDesc && (
                <p className="text-xs text-gray-500">{game.imageDesc}</p>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLocation(`/admin/games/${gameKey}/edit-image`)}
                className="w-full border-2 border-black text-black hover:text-black hover:bg-gray-100"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit Image
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Game Packages */}
        <Card className="bg-white border-2 border-black">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-black">Game Packages</CardTitle>
              <CardDescription className="text-black">{packages.length} packages available</CardDescription>
            </div>
            <Button onClick={handleAddPackage}>
              <Plus className="h-4 w-4 mr-2" />
              Add Package
            </Button>
          </CardHeader>
          <CardContent>
            {isLoadingPackages ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : packages.length === 0 ? (
              <div className="text-center py-8 text-black">
                <p>No packages available for this game</p>
              </div>
            ) : (
              <>
                {selectedPackageIds.size > 0 && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedPackageIds.size} package(s) selected
                    </span>
                    <div className="flex gap-2">
                      <select
                        value={bulkStatus}
                        onChange={(e) => setBulkStatus(e.target.value as "active" | "inactive")}
                        className="px-3 py-2 border border-gray-300 rounded text-sm"
                      >
                        <option value="active">Set to Active</option>
                        <option value="inactive">Set to Inactive</option>
                      </select>
                      <Button
                        size="sm"
                        onClick={() => setShowBulkStatusDialog(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update Status
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setShowBulkDeleteDialog(true)}
                      >
                        Delete Selected
                      </Button>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-black">
                    <TableRow>
                      <TableHead className="w-12 bg-black">
                        <input
                          type="checkbox"
                          checked={selectedPackageIds.size === packages.length && packages.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPackageIds(new Set(packages.map((p: GamePackage) => p.id || "")));
                            } else {
                              setSelectedPackageIds(new Set());
                            }
                          }}
                          className="w-4 h-4"
                        />
                      </TableHead>
                      <TableHead className="text-white bg-black">Package Name</TableHead>
                      <TableHead className="text-white bg-black">Product ID</TableHead>
                      <TableHead className="text-white bg-black">Price</TableHead>
                      <TableHead className="text-white bg-black">Bonus Info</TableHead>
                      <TableHead className="text-white bg-black">Status</TableHead>
                      <TableHead className="text-white bg-black">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {packages.map((pkg: GamePackage) => (
                      <TableRow key={pkg.id} className="hover:bg-gray-100">
                        <TableCell className="w-12">
                          <input
                            type="checkbox"
                            checked={selectedPackageIds.has(pkg.id || "")}
                            onChange={(e) => {
                              const newSelected = new Set(selectedPackageIds);
                              if (e.target.checked) {
                                newSelected.add(pkg.id || "");
                              } else {
                                newSelected.delete(pkg.id || "");
                              }
                              setSelectedPackageIds(newSelected);
                            }}
                            className="w-4 h-4"
                          />
                        </TableCell>
                        <TableCell className="font-medium text-black">{pkg.packageName}</TableCell>
                        <TableCell className="text-sm text-black">{pkg.productId}</TableCell>
                        <TableCell className="font-medium text-black">{pkg.price} C</TableCell>
                        <TableCell className="text-sm text-black">
                          {pkg.bonusInfo || "-"}
                        </TableCell>
                        <TableCell>{getStatusBadge(pkg.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditPackage(pkg)}
                              title="Edit package"
                              className="text-black hover:text-black"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-600"
                              title="Delete package"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Game</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{game.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteGame}
              >
                Delete Game
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Game Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Game</DialogTitle>
              <DialogDescription>
                Update game information
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Game Name</label>
                <Input
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, name: e.target.value })
                  }
                  placeholder="Game name"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Category</label>
                <Input
                  value={editFormData.category}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, category: e.target.value })
                  }
                  placeholder="e.g., RPG, Action"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Tier</label>
                <select
                  value={editFormData.tier}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      tier: e.target.value as "S" | "A" | "B" | "C",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="S">S Tier</option>
                  <option value="A">A Tier</option>
                  <option value="B">B Tier</option>
                  <option value="C">C Tier</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Status</label>
                <select
                  value={editFormData.status}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      status: e.target.value as "active" | "inactive",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, description: e.target.value })
                  }
                  placeholder="Game description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>


            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                disabled={isEditingSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={isEditingSaving}
              >
                {isEditingSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Package Modal */}
        <Dialog open={showPackageModal} onOpenChange={setShowPackageModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedPackage ? "Edit Package" : "Add New Package"}
              </DialogTitle>
              <DialogDescription>
                {selectedPackage
                  ? "Update package details"
                  : "Create a new game package"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Package Name</label>
                <Input
                  value={packageFormData.name}
                  onChange={(e) =>
                    setPackageFormData({ ...packageFormData, name: e.target.value })
                  }
                  placeholder="e.g., Premium Card 299 บาท"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Price (C)</label>
                <Input
                  type="number"
                  value={packageFormData.price}
                  onChange={(e) =>
                    setPackageFormData({ ...packageFormData, price: e.target.value })
                  }
                  placeholder="e.g., 299"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Bonus Info</label>
                <Input
                  value={packageFormData.bonusInfo}
                  onChange={(e) =>
                    setPackageFormData({
                      ...packageFormData,
                      bonusInfo: e.target.value,
                    })
                  }
                  placeholder="e.g., ได้รับ 300 Jade"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Status</label>
                <select
                  value={packageFormData.status}
                  onChange={(e) =>
                    setPackageFormData({
                      ...packageFormData,
                      status: e.target.value as "active" | "inactive",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowPackageModal(false)}
                disabled={isSavingPackage}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSavePackage}
                disabled={isSavingPackage}
              >
                {isSavingPackage ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  `${selectedPackage ? "Update" : "Create"} Package`
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Status Update Confirmation Dialog */}
        <Dialog open={showBulkStatusDialog} onOpenChange={setShowBulkStatusDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Package Status</DialogTitle>
              <DialogDescription>
                Update status for {selectedPackageIds.size} package(s) to "{bulkStatus}"?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowBulkStatusDialog(false)}
                disabled={isBulkUpdating}
              >
                Cancel
              </Button>
              <Button 
                onClick={async () => {
                  setIsBulkUpdating(true);
                  try {
                    let successCount = 0;
                    for (const packageId of Array.from(selectedPackageIds)) {
                      try {
                        await updatePackageMutation.mutateAsync({
                          gameId: game?.id || "",
                          packageId,
                          status: bulkStatus,
                        });
                        successCount++;
                      } catch (error) {
                        console.error(`Failed to update package ${packageId}:`, error);
                      }
                    }
                    alert(`${successCount} package(s) updated to ${bulkStatus}!`);
                    setShowBulkStatusDialog(false);
                    setSelectedPackageIds(new Set());
                    await refetchPackages();
                  } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : "Failed to update packages";
                    alert(`Error: ${errorMessage}`);
                  } finally {
                    setIsBulkUpdating(false);
                  }
                }}
                disabled={isBulkUpdating}
              >
                {isBulkUpdating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Packages</DialogTitle>
              <DialogDescription>
                Delete {selectedPackageIds.size} package(s)? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowBulkDeleteDialog(false)}
                disabled={isBulkUpdating}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={async () => {
                  setIsBulkDeleting(true);
                  try {
                    let successCount = 0;
                    for (const packageId of Array.from(selectedPackageIds)) {
                      try {
                        await deletePackageMutation.mutateAsync({
                          gameId: game?.id || "",
                          packageId,
                        });
                        successCount++;
                      } catch (error) {
                        console.error(`Failed to delete package ${packageId}:`, error);
                      }
                    }
                    alert(`${successCount} package(s) deleted!`);
                    setShowBulkDeleteDialog(false);
                    setSelectedPackageIds(new Set());
                    await refetchPackages();
                  } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : "Failed to delete packages";
                    alert(`Error: ${errorMessage}`);
                  } finally {
                    setIsBulkDeleting(false);
                  }
                }}
                disabled={isBulkUpdating}
              >
                {isBulkUpdating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
